import React, { createContext, useState, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import {
  createTheme,
  ThemeProvider as MUIThemeProvider,
  StyledEngineProvider,
} from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(
    localStorage.getItem("darkMode") === "true"
  );

  const toggleTheme = () => {
    setDarkMode((prevMode) => {
      const newMode = !prevMode;
      localStorage.setItem("darkMode", newMode);
      return newMode;
    });
  };

  const theme = useMemo(
    () => {
      const palette = {
        mode: darkMode ? "dark" : "light",
        primary: {
          main: "#00a884",
          dark: "#008f72",
          light: "#25d366",
          contrastText: "#ffffff",
        },
        success: {
          main: "#00a884",
          contrastText: "#ffffff",
        },
        warning: {
          main: "#f0b429",
          contrastText: "#111b21",
        },
        error: {
          main: "#e5484d",
          contrastText: "#ffffff",
        },
        background: {
          default: darkMode ? "#0b141a" : "#f0f2f5",
          paper: darkMode ? "#111b21" : "#fcfdff",
        },
        text: {
          primary: darkMode ? "#e9edef" : "#111b21",
          secondary: darkMode ? "#8696a0" : "#667781",
        },
        divider: darkMode ? "#2a3942" : "#dbe3e8",
      };

      return createTheme({
        palette,
        shape: {
          borderRadius: 10,
        },
        spacing: 8,
        typography: {
          fontFamily: '"Segoe UI", "Helvetica Neue", Arial, sans-serif',
          fontSize: 15,
          h1: { fontSize: "2rem", lineHeight: 1.35, fontWeight: 600 },
          h2: { fontSize: "1.75rem", lineHeight: 1.35, fontWeight: 600 },
          h3: { fontSize: "1.5rem", lineHeight: 1.4, fontWeight: 600 },
          h4: { fontSize: "1.25rem", lineHeight: 1.4, fontWeight: 600 },
          h5: { fontSize: "1.125rem", lineHeight: 1.45, fontWeight: 600 },
          h6: { fontSize: "1rem", lineHeight: 1.45, fontWeight: 600 },
          body1: { fontSize: "1rem", lineHeight: 1.5 },
          body2: { fontSize: "0.9375rem", lineHeight: 1.5 },
          caption: { fontSize: "0.8125rem", lineHeight: 1.45 },
          button: {
            textTransform: "none",
            fontWeight: 500,
            letterSpacing: 0,
          },
        },
        scrollbarStyles: {
          "&::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: darkMode ? "#3a4a54" : "#c8d1d7",
            borderRadius: "999px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              "*, *::before, *::after": {
                boxSizing: "border-box",
              },
              html: {
                width: "100%",
              },
              body: {
                width: "100%",
                margin: 0,
                overflow: "hidden",
                backgroundColor: palette.background.default,
                color: palette.text.primary,
              },
              "#root": {
                width: "100%",
                minHeight: "100vh",
              },
              "*:focus-visible": {
                outline: `2px solid ${palette.primary.main}`,
                outlineOffset: 2,
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: "none",
                borderRadius: 10,
                border: `1px solid ${palette.divider}`,
              },
              elevation0: {
                border: "none",
              },
            },
          },
          MuiButton: {
            defaultProps: {
              disableElevation: true,
            },
            styleOverrides: {
              root: {
                minHeight: 40,
                padding: "8px 14px",
                borderRadius: 10,
                transition: "all 220ms ease",
              },
              outlined: {
                borderColor: palette.divider,
              },
              containedPrimary: {
                boxShadow: "none",
                "&:hover": {
                  boxShadow: "none",
                },
              },
            },
          },
          MuiIconButton: {
            styleOverrides: {
              root: {
                width: 40,
                height: 40,
                color: palette.text.secondary,
                transition: "background-color 180ms ease, color 180ms ease",
                "&:hover": {
                  backgroundColor: darkMode ? "rgba(134,150,160,0.12)" : "rgba(102,119,129,0.12)",
                },
              },
              sizeSmall: {
                width: 34,
                height: 34,
              },
            },
          },
          MuiInputBase: {
            styleOverrides: {
              root: {
                borderRadius: 10,
              },
              input: {
                fontSize: "1rem",
                ["@media (max-width:768px)"]: {
                  fontSize: "1rem",
                },
              },
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                borderRadius: 10,
                backgroundColor: darkMode ? "#0f1a20" : "#ffffff",
                transition: "background-color 180ms ease, border-color 180ms ease",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: palette.divider,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: palette.text.secondary,
                },
                "&:hover": {
                  backgroundColor: darkMode ? "#122029" : "#f8fafc",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: palette.primary.main,
                },
                "&.Mui-focused": {
                  backgroundColor: darkMode ? "#122029" : "#ffffff",
                },
              },
            },
          },
          MuiInputAdornment: {
            styleOverrides: {
              root: {
                color: palette.text.secondary,
              },
            },
          },
          MuiFormLabel: {
            styleOverrides: {
              root: {
                color: palette.text.secondary,
                "&.Mui-focused": {
                  color: palette.primary.main,
                },
              },
            },
          },
          MuiDialogTitle: {
            styleOverrides: {
              root: {
                padding: "16px 20px",
                borderBottom: `1px solid ${palette.divider}`,
                fontWeight: 600,
              },
            },
          },
          MuiDialogContent: {
            styleOverrides: {
              root: {
                padding: "16px 20px",
                "&.MuiDialogContent-dividers": {
                  borderTopColor: palette.divider,
                  borderBottomColor: palette.divider,
                },
              },
            },
          },
          MuiDialogActions: {
            styleOverrides: {
              root: {
                padding: "12px 20px 16px",
                gap: 8,
              },
            },
          },
          MuiTextField: {
            defaultProps: {
              variant: "outlined",
              margin: "dense",
            },
          },
          MuiTab: {
            styleOverrides: {
              root: {
                minHeight: 56,
                transition: "color 180ms ease, background-color 180ms ease",
                "&:hover": {
                  backgroundColor: darkMode ? "rgba(42,57,66,0.45)" : "rgba(0,0,0,0.04)",
                },
              },
            },
          },
          MuiDrawer: {
            styleOverrides: {
              paper: {
                borderRight: `1px solid ${palette.divider}`,
              },
            },
          },
          MuiDialog: {
            styleOverrides: {
              paper: {
                borderRadius: 14,
                border: `1px solid ${palette.divider}`,
                backgroundColor: palette.background.paper,
              },
            },
          },
          MuiMenu: {
            styleOverrides: {
              paper: {
                borderRadius: 10,
                border: `1px solid ${palette.divider}`,
              },
            },
          },
        },
      });
    },
    [darkMode]
  );

  const contextValue = useMemo(() => ({ darkMode, toggleTheme }), [darkMode]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <StyledEngineProvider injectFirst>
        <MUIThemeProvider theme={theme}>
          <CssBaseline />
          {children}
        </MUIThemeProvider>
      </StyledEngineProvider>
    </ThemeContext.Provider>
  );
};
ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useThemeContext = () => useContext(ThemeContext);
